import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { calcSensorHex } from '@helpers/helper';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem } from '@model/actionItem';

import { getInstPointInfo } from '@pages/ActionItem/helpers/getInstPointInfo';

import { apiClient } from '@services/apiClient';

export const actionItemKey = (actionItemId: string | number) => ['actionItem', +actionItemId];

export const actionItemFn = (queryKey: ReturnType<typeof actionItemKey>, signal: AbortSignal | undefined) =>
  apiClient.get<{ data: IActionItem }>(`${APP_API_URL_V2}fsd/action-item/${queryKey[1]}`, {
    signal,
  });

const actionItemSelect = (t: (text: string) => string, data: IActionItem) => {
  if (!data) return undefined;

  const hasDescription = !!data.description?.replace(/<(?!img\b)[^>]+>/gi, '')?.trim().length;
  const descriptionInfo = data?.description?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  const isSensorMote = data.equipment_type === 'mote' || data.equipment_type === 'sensor';
  const point = data?.installation_point;
  const equipmentName = point.equipment?.name || data?.equipment_info || '-';
  const locationName = data.installation_point?.equipment?.location?.name || data.location_info || '-';
  const instPointInfo = getInstPointInfo(point, data?.equipment_type, t);

  const technicalInfo: string[] = [
    `${t('Title')}: ${data?.title}`,
    `${t('Installation Point')}: ${instPointInfo || '-'}`,
    isSensorMote
      ? `${t('Hex Code')}: ${calcSensorHex(null, data?.sensor_id)}`
      : `${t('Serial')}: ${data?.serial}`,
    `${t('Location')}: ${locationName}`,
    `${t('Equipment')}: ${equipmentName}`,
    `${t('Facility Name')}: ${data?.facility?.name || '-'}`,
    `${t('Node')}: ${data?.installation_point?.sensor?.node?.serial || '-'}`,
    `${t('Asset Code')}: ${point?.equipment?.asset_code || '-'}`,
    `${t('Work Order')}: ${data.wo_number || '-'}`,
  ];

  return {
    ...data,
    hasDescription,
    technicalInfo,
    equipmentName,
    locationName,
    instPointInfo,
    descriptionInfo,
  };
};

export const useActionItemQuery = ({ actionItemId }: { actionItemId: string | number }) => {
  const { t } = useTranslation();

  return useQuery({
    // @ts-ignore TODO: update react-query to v5
    queryKey: actionItemKey(actionItemId),
    queryFn: ({ queryKey, signal }: QueryFunctionContext<ReturnType<typeof actionItemKey>>) =>
      actionItemFn(queryKey, signal),
    enabled: !!actionItemId && actionItemId !== 'onboarding',
    useErrorBoundary: true,
    cacheTime: 1000,
    select: ({ data }: AxiosResponse<{ data: IActionItem }>) => actionItemSelect(t, data?.data),
    placeholderData: {
      data: {},
    },
  });
};
